import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { roundToDecimals } from '../../../../../utils/math/number';
import {
  fractionFromSlopePercent,
  slopePercentToSignedDegrees,
} from '../../../../../utils/math/slope';
import {
  SnapSettingStatus,
  TooltipSnapSettings,
} from '../../../../../viewer/tools/utils/snap-settings-status';
import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';

const format = (value: number) => roundToDecimals(value, 2).toString();

/**
 * Formats the slope as it is entered: in percent, in degrees or as a height to width ratio.
 */
const formatIncline = (
  mode: Extract<SnapSettingStatus, { setting: 'incline' }>['mode'],
  percent: number,
) => {
  switch (mode) {
    case 'slopePercent':
      return `${format(percent)} %`;
    case 'slopeDegrees':
      return `${format(slopePercentToSignedDegrees(percent))}°`;
    case 'slopeRatio': {
      const { x, y } = fractionFromSlopePercent(percent);
      return `${percent < 0 ? '-' : ''}${format(y)} : ${format(x)}`;
    }
  }
};

/**
 * Lists the snap settings of what is snapped, a point, a line or an offset, with their values. A
 * setting that is not used, as another one decides it, is struck through in red, e.g. the height of
 * a line at a slope.
 */
export const SnapSettingsInfo = (props: { snapSettings: TooltipSnapSettings }) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const length = (value: number, signed = false) =>
    `${signed && value > 0 ? '+' : ''}${value.toFixed(2)} ${unitSymbols.length}`;

  const titles: Record<TooltipSnapSettings['action'], string> = {
    point: t('assetPositionTooltip.snapSettings.title.point', { ns: 'skyview' }),
    line: t('assetPositionTooltip.snapSettings.title.line', { ns: 'skyview' }),
    offset: t('assetPositionTooltip.snapSettings.title.offset', { ns: 'skyview' }),
  };

  const describe = (status: SnapSettingStatus): { label: string; value?: string } => {
    switch (status.setting) {
      case 'height':
        return status.mode === 'absolute'
          ? {
              label: t('section.snapping.height.text', { ns: 'skyviewDesign' }),
              value: `${t('section.snapping.height.absolute.text', { ns: 'skyviewDesign' })} ${length(status.value)}`,
            }
          : {
              label: t('section.snapping.height.offset.text', { ns: 'skyviewDesign' }),
              value: length(status.value, true),
            };
      case 'angle':
        return {
          label: t('section.snapping.lines.angle.text', { ns: 'skyviewDesign' }),
          value:
            status.mode === 'custom'
              ? `${format(status.value)}°`
              : t('section.snapping.lines.angle.perpendicular.text', { ns: 'skyviewDesign' }),
        };
      case 'length':
        switch (status.mode) {
          case '2d':
            return {
              label: t('section.snapping.lines.length.twoD.inputTitle', { ns: 'skyviewDesign' }),
              value: length(status.value),
            };
          case '3d':
            return {
              label: t('section.snapping.lines.length.threeD.inputTitle', { ns: 'skyviewDesign' }),
              value: length(status.value),
            };
          case 'cutFill':
            return {
              label: t('section.snapping.lines.length.cutFill.text', { ns: 'skyviewDesign' }),
            };
        }
        break;
      case 'incline':
        return {
          label: t('section.snapping.lines.incline.text', { ns: 'skyviewDesign' }),
          value: formatIncline(status.mode, status.value),
        };
    }
  };

  return (
    <Component data-testid="snap-settings">
      <label>{titles[props.snapSettings.action]}</label>
      {props.snapSettings.settings.map((status) => {
        const { label, value } = describe(status);

        return (
          <Row data-used={status.used} key={status.setting} used={status.used}>
            <Label>{label}</Label>
            {value && <span>{value}</span>}
          </Row>
        );
      })}
    </Component>
  );
};

const Component = styled.div`
  margin-top: 0.5em;
  padding-top: 0.5em;
  // Faded rather than colored, so that it suits a light background in the menu too.
  border-top: 1px solid rgba(128, 128, 128, 0.6);

  > label {
    display: block;
    opacity: 0.67;
    font-size: 0.9em;
    margin-bottom: 0.3em;
  }
`;

const Row = styled.div<{ used: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 0.4em;
  padding: 0.15em 0;
  white-space: nowrap;
  color: ${(props) => (props.used ? 'inherit' : props.theme.color.red)};

  > span {
    text-decoration: ${(props) => (props.used ? 'none' : 'line-through')};
  }
`;

const Label = styled.span`
  min-width: 6.5em;
  opacity: 0.67;
`;
